import { Box, Button, Divider, Grid, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelConfirmDialog } from '../../../components';
import { validateNumberFormat } from '../../../utils/number-validation';
import { createDecimalKeyDownHandler } from '../../../utils/number-input-handlers';

// Types for Engineering Settings
export interface EngineeringSettingsData {
  openPoleBarnFee: number;
  partialClosedPoleBarnFee: number;
  fullyEnclosedPoleBarnFee: number;
  permanentDwellingNoMepFee: number;
}

// Typed enum for engineering field identifiers to prevent typos
enum EngineeringField {
  OpenPoleBarn = 'openPoleBarn',
  PartialClosedPoleBarn = 'partialClosedPoleBarn',
  FullyEnclosedPoleBarn = 'fullyEnclosedPoleBarn',
  PermanentDwellingNoMep = 'permanentDwellingNoMep',
}

export interface EngineeringSettingsProps {
  initialData?: EngineeringSettingsData;
  onSave: (data: EngineeringSettingsData) => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

const EngineeringSettings: React.FC<EngineeringSettingsProps> = ({
  initialData,
  onSave,
  isSaving = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [openPoleBarnFee, setOpenPoleBarnFee] = useState<number | null | undefined>(
    initialData?.openPoleBarnFee ?? null
  );
  const [partialClosedPoleBarnFee, setPartialClosedPoleBarnFee] = useState<
    number | null | undefined
  >(initialData?.partialClosedPoleBarnFee ?? null);
  const [fullyEnclosedPoleBarnFee, setFullyEnclosedPoleBarnFee] = useState<
    number | null | undefined
  >(initialData?.fullyEnclosedPoleBarnFee ?? null);
  const [permanentDwellingNoMepFee, setPermanentDwellingNoMepFee] = useState<
    number | null | undefined
  >(initialData?.permanentDwellingNoMepFee ?? null);
  const [engineeringErrors, setEngineeringErrors] = useState<{ [key: string]: string }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});

  // Sync state from initialData only on initial mount or when switching manufacturers
  const prevInitialDataRef = React.useRef(initialData);
  useEffect(() => {
    // Only update if this is the first load (prevRef is null/undefined)
    // OR if the manufacturer/component changed (completely different data)
    // OR if initialData changed from a value to undefined/null (manufacturer with no settings)
    const isInitialLoad = !prevInitialDataRef.current;
    const dataCleared = prevInitialDataRef.current && !initialData;
    const dataChanged =
      prevInitialDataRef.current &&
      initialData &&
      (prevInitialDataRef.current.openPoleBarnFee !== initialData.openPoleBarnFee ||
        prevInitialDataRef.current.partialClosedPoleBarnFee !==
          initialData.partialClosedPoleBarnFee ||
        prevInitialDataRef.current.fullyEnclosedPoleBarnFee !==
          initialData.fullyEnclosedPoleBarnFee ||
        prevInitialDataRef.current.permanentDwellingNoMepFee !==
          initialData.permanentDwellingNoMepFee);

    if (initialData && (isInitialLoad || dataChanged)) {
      setOpenPoleBarnFee(initialData.openPoleBarnFee);
      setPartialClosedPoleBarnFee(initialData.partialClosedPoleBarnFee);
      setFullyEnclosedPoleBarnFee(initialData.fullyEnclosedPoleBarnFee);
      setPermanentDwellingNoMepFee(initialData.permanentDwellingNoMepFee);
      setEngineeringErrors({});
      setHasUnsavedChanges(false);
      setRawInputValues({}); // Clear raw input values when data changes
      prevInitialDataRef.current = initialData;
    } else if (dataCleared) {
      // Reset all fields when data is cleared (e.g., switching to manufacturer with no settings)
      setOpenPoleBarnFee(null);
      setPartialClosedPoleBarnFee(null);
      setFullyEnclosedPoleBarnFee(null);
      setPermanentDwellingNoMepFee(null);
      setEngineeringErrors({});
      setHasUnsavedChanges(false);
      setRawInputValues({});
      prevInitialDataRef.current = initialData;
    }
  }, [initialData]);

  // Handlers
  const handleNumberFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string
  ) => {
    const input = event.target as HTMLInputElement;
    setFocusedField(fieldName);

    // Store the current display value as the raw input when focusing
    const currentValue = input.value;
    setRawInputValues(prev => ({ ...prev, [fieldName]: currentValue }));
    if (input.value === '0' || input.value === '0.00') {
      setTimeout(() => {
        input.select();
      }, 0);
    }
  };

  // Use shared handler for decimal number input validation
  const handleKeyDown = createDecimalKeyDownHandler();

  const handleNumberBlurWithField = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void,
    fieldName: string
  ) => {
    setFocusedField(null);
    handleNumberBlur(event, setter, fieldName);
  };

  const handleNumberBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void,
    fieldName: string
  ) => {
    const value = (event.target as HTMLInputElement).value.trim();

    // Clear the raw input value for this field
    setRawInputValues(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    if (value === '' || value === '.') {
      setter(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Round to 2 decimal places (e.g., 2.541 -> 2.54, 1.8 -> 1.80)
        const roundedValue = Math.round(numValue * 100) / 100;
        setter(roundedValue);
      } else {
        setter(null);
      }
    }
  };

  const handleFeeChange = (field: EngineeringField, inputValue: string) => {
    setHasUnsavedChanges(true);

    // Store the raw input value while the field is focused
    if (focusedField === field) {
      setRawInputValues(prev => ({ ...prev, [field]: inputValue }));
    }

    // Allow empty string or just a decimal point during editing (for partial input)
    if (inputValue === '' || inputValue === '.') {
      const errors = { ...engineeringErrors };
      delete errors[field];
      setEngineeringErrors(errors);
      // Don't update the numeric state, just keep the raw input
      return;
    }

    // Validate format (allow decimals, but prevent leading zeros and scientific notation)
    // For decimals, we need to allow partial input like "1." or "0."
    // So we check format validation only when the input is complete
    const trimmed = inputValue.trim();
    const isPartialDecimal = trimmed.endsWith('.') && trimmed.length > 1;

    // If it's not a partial decimal, validate the format
    if (!isPartialDecimal) {
      const formatValidation = validateNumberFormat(inputValue, true);
      const errors = { ...engineeringErrors };

      if (!formatValidation.isValid) {
        errors[field] = formatValidation.error || 'Invalid number format';
        setEngineeringErrors(errors);
        // Don't update the numeric state if format is invalid
        return;
      }
    }

    // Parse the input value - allow partial numbers during typing
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) {
      // If not a valid number and not a partial decimal, show error
      if (!isPartialDecimal) {
        const errors = { ...engineeringErrors };
        errors[field] = 'Please enter a valid number';
        setEngineeringErrors(errors);
      }
      return;
    }

    // Validation
    const errors = { ...engineeringErrors };
    if (numValue < 0) {
      errors[field] = 'Fee amount must be at least $0.00';
    } else {
      delete errors[field];
    }
    setEngineeringErrors(errors);

    // Update the appropriate state using typed enum
    switch (field) {
      case EngineeringField.OpenPoleBarn:
        setOpenPoleBarnFee(numValue);
        break;
      case EngineeringField.PartialClosedPoleBarn:
        setPartialClosedPoleBarnFee(numValue);
        break;
      case EngineeringField.FullyEnclosedPoleBarn:
        setFullyEnclosedPoleBarnFee(numValue);
        break;
      case EngineeringField.PermanentDwellingNoMep:
        setPermanentDwellingNoMepFee(numValue);
        break;
    }
  };

  const handleSave = () => {
    const isEngineeringValid = Object.keys(engineeringErrors).length === 0;

    if (!isEngineeringValid) {
      return;
    }

    const saveData: EngineeringSettingsData = {
      openPoleBarnFee: openPoleBarnFee ?? 0,
      partialClosedPoleBarnFee: partialClosedPoleBarnFee ?? 0,
      fullyEnclosedPoleBarnFee: fullyEnclosedPoleBarnFee ?? 0,
      permanentDwellingNoMepFee: permanentDwellingNoMepFee ?? 0,
    };

    onSave(saveData);
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      handleCancelConfirm();
    }
  };

  const handleCancelConfirm = () => {
    setOpenPoleBarnFee(initialData?.openPoleBarnFee ?? null);
    setPartialClosedPoleBarnFee(initialData?.partialClosedPoleBarnFee ?? null);
    setFullyEnclosedPoleBarnFee(initialData?.fullyEnclosedPoleBarnFee ?? null);
    setPermanentDwellingNoMepFee(initialData?.permanentDwellingNoMepFee ?? null);
    setEngineeringErrors({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    navigate('/');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Engineered Plan Fees
        </Typography>

        <Grid container spacing={3}>
          {/* Open Pole Barn */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Open Pole Barn"
                  type="text"
                  value={
                    focusedField === EngineeringField.OpenPoleBarn
                      ? (rawInputValues[EngineeringField.OpenPoleBarn] ??
                        (openPoleBarnFee === null || openPoleBarnFee === undefined
                          ? ''
                          : openPoleBarnFee.toString()))
                      : openPoleBarnFee === null || openPoleBarnFee === undefined
                        ? ''
                        : openPoleBarnFee.toFixed(2)
                  }
                  onChange={e => handleFeeChange(EngineeringField.OpenPoleBarn, e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, EngineeringField.OpenPoleBarn)}
                  onBlur={e =>
                    handleNumberBlurWithField(e, setOpenPoleBarnFee, EngineeringField.OpenPoleBarn)
                  }
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={readOnly}
                  error={!!engineeringErrors[EngineeringField.OpenPoleBarn]}
                  helperText={engineeringErrors[EngineeringField.OpenPoleBarn]}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Engineering fee for open pole barn structures intended for permanent dwelling use.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Partial Closed Pole Barn */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Partial Closed Pole Barn"
                  type="text"
                  value={
                    focusedField === EngineeringField.PartialClosedPoleBarn
                      ? (rawInputValues[EngineeringField.PartialClosedPoleBarn] ??
                        (partialClosedPoleBarnFee === null || partialClosedPoleBarnFee === undefined
                          ? ''
                          : partialClosedPoleBarnFee.toString()))
                      : partialClosedPoleBarnFee === null || partialClosedPoleBarnFee === undefined
                        ? ''
                        : partialClosedPoleBarnFee.toFixed(2)
                  }
                  onChange={e =>
                    handleFeeChange(EngineeringField.PartialClosedPoleBarn, e.target.value)
                  }
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, EngineeringField.PartialClosedPoleBarn)}
                  onBlur={e =>
                    handleNumberBlurWithField(
                      e,
                      setPartialClosedPoleBarnFee,
                      EngineeringField.PartialClosedPoleBarn
                    )
                  }
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={readOnly}
                  error={!!engineeringErrors[EngineeringField.PartialClosedPoleBarn]}
                  helperText={engineeringErrors[EngineeringField.PartialClosedPoleBarn]}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Engineering fee for partially enclosed pole barn structures intended for permanent
                  dwelling use.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Fully Enclosed Pole Barn */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Fully Enclosed Pole Barn"
                  type="text"
                  value={
                    focusedField === EngineeringField.FullyEnclosedPoleBarn
                      ? (rawInputValues[EngineeringField.FullyEnclosedPoleBarn] ??
                        (fullyEnclosedPoleBarnFee === null || fullyEnclosedPoleBarnFee === undefined
                          ? ''
                          : fullyEnclosedPoleBarnFee.toString()))
                      : fullyEnclosedPoleBarnFee === null || fullyEnclosedPoleBarnFee === undefined
                        ? ''
                        : fullyEnclosedPoleBarnFee.toFixed(2)
                  }
                  onChange={e =>
                    handleFeeChange(EngineeringField.FullyEnclosedPoleBarn, e.target.value)
                  }
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, EngineeringField.FullyEnclosedPoleBarn)}
                  onBlur={e =>
                    handleNumberBlurWithField(
                      e,
                      setFullyEnclosedPoleBarnFee,
                      EngineeringField.FullyEnclosedPoleBarn
                    )
                  }
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={readOnly}
                  error={!!engineeringErrors[EngineeringField.FullyEnclosedPoleBarn]}
                  helperText={engineeringErrors[EngineeringField.FullyEnclosedPoleBarn]}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Engineering fee for fully enclosed pole barn structures intended for permanent
                  dwelling use.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Permanent Dwelling (no MEP) */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Permanent Dwelling (no MEP)"
                  type="text"
                  value={
                    focusedField === EngineeringField.PermanentDwellingNoMep
                      ? (rawInputValues[EngineeringField.PermanentDwellingNoMep] ??
                        (permanentDwellingNoMepFee === null ||
                        permanentDwellingNoMepFee === undefined
                          ? ''
                          : permanentDwellingNoMepFee.toString()))
                      : permanentDwellingNoMepFee === null ||
                          permanentDwellingNoMepFee === undefined
                        ? ''
                        : permanentDwellingNoMepFee.toFixed(2)
                  }
                  onChange={e =>
                    handleFeeChange(EngineeringField.PermanentDwellingNoMep, e.target.value)
                  }
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, EngineeringField.PermanentDwellingNoMep)}
                  onBlur={e =>
                    handleNumberBlurWithField(
                      e,
                      setPermanentDwellingNoMepFee,
                      EngineeringField.PermanentDwellingNoMep
                    )
                  }
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={readOnly}
                  error={!!engineeringErrors[EngineeringField.PermanentDwellingNoMep]}
                  helperText={engineeringErrors[EngineeringField.PermanentDwellingNoMep]}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Engineering fee for permanent dwelling structures without Mechanical, Electrical,
                  and Plumbing (MEP) systems.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isSaving || readOnly}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !hasUnsavedChanges || Object.keys(engineeringErrors).length > 0 || isSaving || readOnly
          }
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <CancelConfirmDialog
        open={showCancelDialog}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setShowCancelDialog(false)}
        dialogTitle="Cancel Changes?"
        dialogContent="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        continuePrompt="Stay"
        cancelPrompt="Leave"
      />
    </Box>
  );
};

export default EngineeringSettings;
